import React, { useState, useEffect } from 'react';
import './AdaptiveFoundationPage.css';

const AdaptiveFoundationPage = () => {
  const [activeCapability, setActiveCapability] = useState(0);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const capabilities = [
    { icon: '🧠', title: 'Understand', description: 'Make sense of unfamiliar situations' },
    { icon: '📚', title: 'Learn', description: 'Continue learning independently' },
    { icon: '💭', title: 'Think', description: 'Evaluate information critically' },
    { icon: '💬', title: 'Communicate', description: 'Cooperate with others effectively' },
    { icon: '🔧', title: 'Solve', description: 'Address unfamiliar problems' },
    { icon: '⚡', title: 'Act', description: 'Make informed decisions' },
    { icon: '🔄', title: 'Adapt', description: 'Thrive when circumstances change' },
  ];

  const levels = [
    { range: '1-4', title: 'Learn English', description: 'Build foundational communication skills', icon: '📚' },
    { range: '5-10', title: 'Understand the World', description: 'Explore interconnected global systems', icon: '🌍' },
    { range: '11', title: 'Change the World', description: 'Lead with purpose and impact', icon: '🚀' },
  ];

  const bigQuestions = [
    'How does the planet work?',
    'How do humans stay healthy?',
    'How are resources created and shared?',
    'How does technology shape our lives?',
    'How do humans organize themselves?',
    'Who are we and what gives life meaning?',
    'What can I do to improve the world?',
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="adaptive-foundation-page">
      {/* Hero Section */}
      <section className="af-hero" style={{ transform: `translateY(${scrollY * 0.5}px)` }}>
        <div className="af-hero-gradient"></div>
        <div className="af-hero-content">
          <div className="af-hero-accent" style={{ transform: `translateY(${scrollY * 0.3}px)` }}></div>
          <h1 className="af-hero-title">
            <span className="af-gradient-text">Adaptive Foundation</span>
          </h1>
          <p className="af-hero-subtitle">
            Building capabilities to thrive in a rapidly changing world
          </p>
          <div className="af-hero-tagline">
            <span>English</span> → <span>Understanding</span> → <span>Action</span>
          </div>
          <div className="af-hero-ctas">
            <button className="af-btn af-btn-primary" onClick={() => scrollToSection('framework')}>
              Explore the Framework
            </button>
            <button className="af-btn af-btn-secondary" onClick={() => scrollToSection('curriculum')}>
              Download Curriculum
            </button>
          </div>
        </div>
      </section>

      {/* Why This Framework */}
      <section className="af-section af-why">
        <div className="af-container">
          <h2>Why This Framework Exists</h2>
          <p className="af-section-intro">
            Young people are growing up in a world of rapid technological, economic, environmental and social change. We cannot reliably predict the jobs they will do, the technologies they will use, or the challenges they will face.
          </p>
          <div className="af-why-grid">
            <div className="af-why-card">
              <div className="af-icon-large">🌍</div>
              <h3>Unpredictable Future</h3>
              <p>Education must prepare learners not just for a predicted future, but to navigate multiple possible futures.</p>
            </div>
            <div className="af-why-card">
              <div className="af-icon-large">🎯</div>
              <h3>Purpose of Education</h3>
              <p>The goal is not simply to transfer knowledge, but to increase learners' ability to understand the world and act effectively within it.</p>
            </div>
            <div className="af-why-card">
              <div className="af-icon-large">🔑</div>
              <h3>English as a Tool</h3>
              <p>English is not the final objective—it's an enabling capability through which learners explore interconnected knowledge.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Framework Overview */}
      <section id="framework" className="af-section af-framework">
        <div className="af-container">
          <h2>The Adaptive Foundation</h2>
          <p className="af-section-intro">
            Seven interconnected capabilities that enable learners to thrive in uncertainty
          </p>

          <div className="af-framework-flow">
            <div className="af-flow-step">
              <div className="af-flow-label">Rapidly Changing World</div>
              <div className="af-flow-arrow">↓</div>
            </div>
            <div className="af-flow-step">
              <div className="af-flow-label af-highlight">Adaptive Foundation</div>
              <div className="af-flow-arrow">↓</div>
            </div>
            <div className="af-flow-step">
              <div className="af-flow-label">Learn → Live → Lead</div>
            </div>
          </div>

          <div className="af-capabilities-grid">
            {capabilities.map((cap, index) => (
              <div
                key={index}
                className={`af-capability-card ${activeCapability === index ? 'active' : ''}`}
                onClick={() => setActiveCapability(index)}
              >
                <div className="af-cap-icon">{cap.icon}</div>
                <h3>{cap.title}</h3>
                <p>{cap.description}</p>
              </div>
            ))}
          </div>

          <div className="af-capabilities-highlight">
            <p className="af-capability-text">
              <strong>Understand</strong> • <strong>Learn</strong> • <strong>Think</strong> • <strong>Communicate</strong> • <strong>Solve</strong> • <strong>Act</strong> • <strong>Adapt</strong>
            </p>
          </div>
        </div>
      </section>

      {/* Three Stages */}
      <section className="af-section af-stages">
        <div className="af-container">
          <h2>Learn → Live → Lead</h2>
          <p className="af-section-intro">Progressive transition from receiving education to becoming an independent learner</p>

          <div className="af-stages-grid">
            {levels.map((level, index) => (
              <div key={index} className={`af-stage-card af-stage-${index + 1}`}>
                <div className="af-stage-badge">{level.range}</div>
                <h3>{level.title}</h3>
                <p>{level.description}</p>
                <div className="af-stage-icon">{level.icon}</div>
              </div>
            ))}
          </div>

          <div className="af-stages-description">
            <div className="af-stage-detail">
              <h4>Level 1-4: LEARN</h4>
              <p>Build knowledge and capabilities. Develop communication skills, access information, and build foundations for understanding complex ideas.</p>
            </div>
            <div className="af-stage-detail">
              <h4>Level 5-10: LIVE</h4>
              <p>Connect learning with life. Apply knowledge to personal decisions, unfamiliar situations, and real-world problems.</p>
            </div>
            <div className="af-stage-detail">
              <h4>Level 11: LEAD</h4>
              <p>Use learning with others. Cooperate, teach, participate in projects, and contribute to solving community problems.</p>
            </div>
          </div>
        </div>
      </section>

      {/* The Big Questions */}
      <section className="af-section af-questions">
        <div className="af-container">
          <h2>The Questions That Matter</h2>
          <p className="af-section-intro">
            Learning is organized around big questions that every young person eventually faces
          </p>

          <div className="af-questions-grid">
            {bigQuestions.map((question, index) => (
              <div key={index} className="af-question-card">
                <div className="af-question-number">{index + 1}</div>
                <p>{question}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Philosophy */}
      <section className="af-section af-philosophy">
        <div className="af-container">
          <h2>How We Make It Real</h2>
          <p className="af-section-intro">Four principles that guide the Litvill learning experience</p>

          <div className="af-principles">
            <div className="af-principle">
              <div className="af-principle-icon">1️⃣</div>
              <h3>English is both a communication skill and a tool for learning</h3>
              <p>Not just a subject to study, but the language through which learners explore interconnected systems of nature, health, economy, technology, society, and culture.</p>
            </div>
            <div className="af-principle">
              <div className="af-principle-icon">2️⃣</div>
              <h3>Knowledge must be useful in real life</h3>
              <p>Learning is connected to authentic problems, meaningful projects, and real-world applications that matter to learners and their communities.</p>
            </div>
            <div className="af-principle">
              <div className="af-principle-icon">3️⃣</div>
              <h3>Students learn by doing and explaining, not memorizing</h3>
              <p>Learners observe, investigate, reflect, and communicate their ideas using English as a practical tool, through discussions, projects, and problem-solving.</p>
            </div>
            <div className="af-principle">
              <div className="af-principle-icon">4️⃣</div>
              <h3>Language develops naturally through meaningful communication</h3>
              <p>Rather than isolated language drills, English is acquired through exploring authentic topics, solving problems, participating in discussions, and completing projects.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Competencies Developed */}
      <section className="af-section af-competencies">
        <div className="af-container">
          <h2>Competencies Developed</h2>
          <p className="af-section-intro">Beyond English: A comprehensive approach to 21st-century skills</p>

          <div className="af-competencies-grid">
            <div className="af-comp-column">
              <h4>Communication & Collaboration</h4>
              <ul>
                <li>English communication</li>
                <li>Meaningful communication</li>
                <li>Collaboration</li>
                <li>Leadership</li>
              </ul>
            </div>
            <div className="af-comp-column">
              <h4>Thinking & Problem-Solving</h4>
              <ul>
                <li>Critical thinking</li>
                <li>Creativity</li>
                <li>Problem-solving</li>
                <li>Systems thinking</li>
              </ul>
            </div>
            <div className="af-comp-column">
              <h4>Learning & Resilience</h4>
              <ul>
                <li>Self-directed learning</li>
                <li>Digital literacy</li>
                <li>Reflection</li>
                <li>Resilience</li>
              </ul>
            </div>
            <div className="af-comp-column">
              <h4>Global Awareness</h4>
              <ul>
                <li>Environmental literacy</li>
                <li>Civic responsibility</li>
                <li>Adaptability</li>
                <li>Global perspective</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Curriculum Section */}
      <section id="curriculum" className="af-section af-curriculum">
        <div className="af-container">
          <h2>The Curriculum</h2>
          <p className="af-section-intro">
            A comprehensive 11-level framework designed to transform how young people learn English and engage with the world
          </p>

          <div className="af-curriculum-preview">
            <div className="af-curriculum-content">
              <h3>What's Inside</h3>
              <ul className="af-curriculum-list">
                <li>Complete learning framework across 11 levels</li>
                <li>Detailed progression philosophy and content structure</li>
                <li>Learning objectives and pedagogical approach</li>
                <li>Language skill development strategies</li>
                <li>Digital learning environment specification</li>
                <li>Assessment and progress tracking methodology</li>
                <li>Learner profile and expected outcomes</li>
                <li>Implementation guidance for educators and institutions</li>
              </ul>
              <p className="af-curriculum-note">
                <strong>30 pages</strong> of research-backed educational framework, field-tested with learners in India and ready for adaptation globally.
              </p>
            </div>
            <div className="af-curriculum-download">
              <div className="af-pdf-icon">📄</div>
              <h4>Litvill Curriculum</h4>
              <p>Complete Educational Framework</p>
              <a href="/curriculum_v1.pdf" download className="af-btn af-btn-primary af-btn-large">
                Download PDF
              </a>
              <p className="af-file-info">PDF • 30 pages • 4.5 MB</p>
            </div>
          </div>

          <div className="af-curriculum-sections">
            <div className="af-curriculum-section">
              <h4>📖 Foundation</h4>
              <p>Why this framework exists, learning philosophy, and core principles</p>
            </div>
            <div className="af-curriculum-section">
              <h4>🎯 Goals & Progression</h4>
              <p>Three-stage learning journey: Learn English → Understand the World → Change the World</p>
            </div>
            <div className="af-curriculum-section">
              <h4>🗺️ 11-Level Structure</h4>
              <p>Detailed content progression from beginner to leadership level</p>
            </div>
            <div className="af-curriculum-section">
              <h4>💡 Pedagogy</h4>
              <p>Learning methods, language skill development, and digital tools</p>
            </div>
            <div className="af-curriculum-section">
              <h4>📊 Assessment</h4>
              <p>Progress tracking, evaluation framework, and learner analytics</p>
            </div>
            <div className="af-curriculum-section">
              <h4>✨ Impact</h4>
              <p>Expected learner outcomes and competencies developed</p>
            </div>
          </div>
        </div>
      </section>

      {/* Origin Story */}
      <section className="af-section af-story">
        <div className="af-container">
          <h2>The Beginning: From One Learner to a Movement</h2>

          <div className="af-story-content">
            <p>
              In 2015, an adult English learner working in hospitality in Jaipur began learning English with a European learning designer. As his communication improved, English increasingly became a means of accessing and discussing wider questions about economics, the environment, and global challenges.
            </p>
            <p>
              Over the following decade, this learning journey expanded through higher education, sustainability research, and eventually the creation of educational opportunities for other young people.
            </p>
            <p>
              What began as language learning gradually raised a larger question:
            </p>
            <div className="af-story-highlight">
              <blockquote>
                "What if the purpose of education is not only to teach what we already know, but to increase a learner's capacity to deal with what we cannot predict?"
              </blockquote>
            </div>
            <p>
              The Litvill Educational Framework grew from that question and is now being developed through work with new generations of learners. We are learning from our implementation, measuring learner development, and continuously improving the framework.
            </p>
            <p>
              Our long-term objective is not to keep this approach within Litvill alone. If it works, we want others to use, test, challenge, and improve it. This framework is yours to adapt and evolve.
            </p>
          </div>
        </div>
      </section>

      {/* Join the Movement */}
      <section className="af-section af-join">
        <div className="af-container">
          <h2>Join the Movement</h2>
          <p className="af-section-intro">
            Be part of an educational revolution building adaptive foundations for young changemakers
          </p>

          <div className="af-join-grid">
            <div className="af-join-card">
              <div className="af-join-icon">🎓</div>
              <h3>Educators & Institutions</h3>
              <p>Implement the framework in your school or organization. Adapt it to your context and share what you learn.</p>
              <a href="mailto:info@litvill.com" className="af-link">Get in Touch</a>
            </div>
            <div className="af-join-card">
              <div className="af-join-icon">🤝</div>
              <h3>Partners & Organizations</h3>
              <p>Collaborate with us to develop, test, and refine the framework in different contexts and regions.</p>
              <a href="mailto:info@litvill.com" className="af-link">Explore Partnership</a>
            </div>
            <div className="af-join-card">
              <div className="af-join-icon">👨‍🎓</div>
              <h3>Young Learners</h3>
              <p>Join Litvill and develop the adaptive foundation you need to understand, navigate, and improve the world.</p>
              <a href="/#apply" className="af-link">Apply Now</a>
            </div>
          </div>
        </div>
      </section>

      {/* Closing Statement */}
      <section className="af-section af-closing">
        <div className="af-container">
          <div className="af-closing-content">
            <h2>Learning English is the beginning of the journey.</h2>
            <p>Understanding the world gives it direction.</p>
            <h3>Improving the world gives it purpose.</h3>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="af-section af-footer-cta">
        <div className="af-container">
          <h3>Ready to explore the Adaptive Foundation?</h3>
          <div className="af-footer-buttons">
            <button className="af-btn af-btn-primary" onClick={() => scrollToSection('curriculum')}>
              Download the Curriculum
            </button>
            <a href="mailto:info@litvill.com" className="af-btn af-btn-secondary">
              Learn More
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdaptiveFoundationPage;